<template>
    <div class="credit-card">

        <button-tooltip left :tooltip="$t('payment.delete_warning_title')" buttonClass="card-delete-btn"
            icon @click="deleteCard">
            <v-icon size="16" color="white">mdi-delete-outline</v-icon>
        </button-tooltip>

        <div class="card-chip"></div>

        <div class="card-number">
            <span>****</span>
            <span>****</span>
            <span>****</span>
            <span>{{ card && card.last4 || '****' }}</span>
        </div>

        <div class="card-bottom">
            <div class="card-name">{{ holder }}</div>
            <div class="card-brand" v-if="brandImg">
                <img :src="brandImg" alt="brand" />
            </div>
        </div>

        <kimsa-confirm ref="deleteModal" :title="$t('payment.delete_warning_title')"
            :content="$t('payment.delete_warning_content')" :loading="loadingDelete" :persistent="false"
            @confirm="deleteConfirm" :close-on-confirm="false" :alert-mode="true" />

    </div>
</template>

<script>
    import {mapMutations} from 'vuex'
    import KimsaConfirm from "../KimsaConfirm";
    import ButtonTooltip from "../ButtonTooltip";
    import {db} from '@/utils/Firebase'

    export default {
        name: "PaymentCard",
        components: {KimsaConfirm, ButtonTooltip},
        props:{
            ownerId: {
                type: String,
                required: true
            },
           paymentMethod:{
               type: Object,
               required: true
           }
        },
        data(){
          return {
              loadingDelete: false
          }
        },
        methods:{
            ...mapMutations(['showSnackBar']),
            deleteCard() {
                const vm = this
                vm.$refs.deleteModal.show()
            },
            async deleteConfirm() {
                const vm = this
                await vm.withLoading(async () => {
                    await db.collection('users').doc(vm.ownerId)
                    .update({
                        'stripeAccount.paymentMethod': null
                    })
                    vm.showSnackBar({
                        text: vm.$t('payment.delete_success'),
                        color: 'error'
                    })
                }, 'loadingDelete')
            }
        },
        computed:{
            holder(){
                const vm = this
                if(!vm.paymentMethod || !vm.paymentMethod.billing_details) return ''
                return vm.paymentMethod.billing_details.name
            },
            card(){
                const vm = this
                return vm.paymentMethod && vm.paymentMethod.card || null
            },
            brandImg(){
                const vm = this
                if(!vm.card) return ''
                 /*
                 * American Express, Diners Club, Discover, JCB, MasterCard, UnionPay, Visa, or Unknown
                 * */
                switch(vm.card.brand){
                    case 'amex':
                        return require('@/assets/img/payment_card/american_express.png')
                    case 'diners':
                        return require('@/assets/img/payment_card/diners_club.png')
                    case 'discover':
                        return require('@/assets/img/payment_card/discover.png')
                    case 'jcb':
                        return require('@/assets/img/payment_card/jcb.png')
                    case 'mastercard':
                        return require('@/assets/img/payment_card/mastercard.png')
                    case 'visa':
                        return require('@/assets/img/payment_card/visa.png')
                    default:
                        return require('@/assets/img/payment_card/unknown.png')
                }

            }
        }
    }
</script>

<style scoped>

.credit-card {
    position: relative;
    width: 100%;
    max-width: 480px;
    height: 280px;
    border-radius: 18px;
    background: linear-gradient(135deg, #2E8B6E 0%, #5BAD8C 40%, #4A9E7A 70%, #3D7A5A 100%);
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
    padding: 28px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.credit-card::before {
    content: '';
    position: absolute;
    top: -60px;
    right: -60px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
}

.credit-card::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 60px;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
}

.credit-card >>> .card-delete-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    width: 36px !important;
    height: 36px !important;
    background: var(--v-error-base) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.card-chip {
    width: 46px;
    height: 34px;
    border-radius: 6px;
    background: linear-gradient(135deg, #d4a84b 0%, #f0d070 30%, #c8942a 60%, #e8c455 100%);
    position: relative;
    z-index: 1;
}

.card-chip::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: rgba(0, 0, 0, 0.2);
}

.card-chip::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    background: rgba(0, 0, 0, 0.15);
}

.card-number {
    display: flex;
    gap: 18px;
    font-size: 20px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.92);
    letter-spacing: 0.14em;
    font-family: 'Courier New', monospace;
    position: relative;
    z-index: 1;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.35);
}

.card-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    z-index: 1;
    position: relative;
}

.card-name {
    font-size: 15px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.92);
    letter-spacing: 0.06em;
    font-family: 'Courier New', monospace;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.35);
}

.card-brand {
    flex-shrink: 0;
    background: rgba(0, 0, 100, 0.4);
    border-radius: 6px;
    padding: 5px 10px;
}

.card-brand img {
    display: block;
    height: 22px;
    width: auto;
}

@media (max-width: 599px) {
    .credit-card {
        height: 200px;
        padding: 20px;
    }

    .card-number {
        font-size: 15px;
        gap: 12px;
        letter-spacing: 0.1em;
    }

    .card-name {
        font-size: 13px;
    }
}

</style>
